import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { adminApi } from '../adminApi';
import { formatDate } from '../format';
import {
  PAGE_REFUSAL_CHECKS,
  PAGE_REFUSAL_CODES,
  accessReason,
  checkLabel,
  codeLabel,
  pageRefusalErrorText,
  pageRefusalsPath,
  v1PagesSummary,
  writerLabel,
  type PageRefusalCheck,
  type PageRefusalCode,
  type PageRefusalList,
  type PageRefusalRow,
  type V1PagesLeft,
} from '../pageRefusals';
import { PanelChangeFailuresPanel } from './PanelChangeFailuresPanel';

// Neo → Page refusals (TOM-672, ADR 0395): board page saves the page check
// refused, newest first, from Neo or the owner. A refused page is the owner's
// content, so the admin states an access reason first, as for a transcript;
// the server logs it and withholds the list when it cannot. A row opens the
// save's page with the same reason.

/**
 * Version 1 pages left (TOM-963, ADR 0462): what the upgrade_v1_pages job has
 * not made version 2 yet. Counts and boat names only, so no reason is asked.
 */
function V1PagesPanel() {
  const [left, setLeft] = useState<V1PagesLeft | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let ignore = false;
    adminApi<V1PagesLeft>('/v1/admin/neo/v1-pages')
      .then((res) => {
        if (!ignore) setLeft(res);
      })
      .catch(() => {
        if (!ignore) setFailed(true);
      });
    return () => {
      ignore = true;
    };
  }, []);
  return (
    <div className="admin-info-card" data-testid="v1-pages-left">
      <h2>Version 1 pages left</h2>
      {failed ? (
        <p className="admin-muted">The count could not be read. Try again in a moment.</p>
      ) : left === null ? (
        <p className="admin-loading-row">Loading…</p>
      ) : (
        <>
          <p>{v1PagesSummary(left)}</p>
          {left.boats.length > 0 ? (
            <div className="scroll-x">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Boat</th>
                    <th>On boards</th>
                    <th>Named pages</th>
                  </tr>
                </thead>
                <tbody>
                  {left.boats.map((b) => (
                    <tr key={b.boatId}>
                      <td>{b.boatName ?? b.boatId}</td>
                      <td>{b.boardPages}</td>
                      <td>{b.namedPages}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

interface Filter {
  check?: PageRefusalCheck;
  code?: PageRefusalCode;
}

function errorText(err: unknown): string {
  const e = err as { status?: number; body?: { error?: unknown } };
  return pageRefusalErrorText(
    e.status,
    typeof e.body?.error === 'string' ? e.body.error : undefined
  );
}

export function AdminNeoPageRefusals() {
  const [reason, setReason] = useState('');
  const [openReason, setOpenReason] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>({});
  const [rows, setRows] = useState<PageRefusalRow[] | null>(null);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (openReason === null) return;
    const path = pageRefusalsPath(openReason, filter);
    if (path === null) return;
    let ignore = false;
    setRows(null);
    setNextBefore(null);
    setError(null);
    adminApi<PageRefusalList>(path)
      .then((res) => {
        if (ignore) return;
        setRows(res.refusals);
        setNextBefore(res.nextBefore);
      })
      .catch((err) => {
        if (!ignore) setError(errorText(err));
      });
    return () => {
      ignore = true;
    };
  }, [openReason, filter]);

  async function loadOlder() {
    if (openReason === null || nextBefore === null) return;
    const path = pageRefusalsPath(openReason, { ...filter, before: nextBefore });
    if (path === null) return;
    setLoadingMore(true);
    try {
      const res = await adminApi<PageRefusalList>(path);
      setRows((prev) => [...(prev ?? []), ...res.refusals]);
      setNextBefore(res.nextBefore);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoadingMore(false);
    }
  }

  function open(e: FormEvent) {
    e.preventDefault();
    const r = accessReason(reason);
    if (r !== null) setOpenReason(r);
  }

  if (openReason === null) {
    return (
      <section className="admin-section">
        <header className="admin-section-header">
          <h1>Page refusals</h1>
        </header>
        <p className="admin-hint">
          Board pages that Neo or a boat owner tried to save, and the page check turned down. Each
          one lists every reason it was turned down.
        </p>
        <PanelChangeFailuresPanel />
        <V1PagesPanel />
        <div className="admin-auth-banner">
          Refused pages are the owner&apos;s content. Your access is logged with your account and
          your reason.
        </div>
        <form className="neo-admin-reason" onSubmit={open}>
          <label htmlFor="page-refusal-reason">Reason for viewing</label>
          <input
            id="page-refusal-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. finding out why pages are turned down"
            minLength={3}
            maxLength={200}
            required
          />
          <button type="submit" className="admin-button" disabled={accessReason(reason) === null}>
            View refused pages
          </button>
        </form>
      </section>
    );
  }

  return (
    <section className="admin-section">
      <header className="admin-section-header">
        <h1>Page refusals</h1>
        {rows !== null && <span className="admin-count">{rows.length}</span>}
      </header>
      <div className="admin-auth-banner">Viewing owners&apos; refused pages — access logged.</div>
      <PanelChangeFailuresPanel />
      <V1PagesPanel />

      <form
        className="eval-filters"
        aria-label="Filter refused pages"
        onSubmit={(e) => e.preventDefault()}
      >
        <label>
          Check
          <select
            value={filter.check ?? ''}
            onChange={(e) =>
              setFilter({
                ...filter,
                check: (e.target.value || undefined) as PageRefusalCheck | undefined,
              })
            }
          >
            <option value="">Both</option>
            {PAGE_REFUSAL_CHECKS.map((c) => (
              <option key={c} value={c}>
                {checkLabel(c)}
              </option>
            ))}
          </select>
        </label>
        <label>
          Outcome
          <select
            value={filter.code ?? ''}
            onChange={(e) =>
              setFilter({
                ...filter,
                code: (e.target.value || undefined) as PageRefusalCode | undefined,
              })
            }
          >
            <option value="">Any</option>
            {PAGE_REFUSAL_CODES.map((c) => (
              <option key={c} value={c}>
                {codeLabel(c)}
              </option>
            ))}
          </select>
        </label>
      </form>

      {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}
      {rows === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {rows !== null ? (
        rows.length === 0 ? (
          <p className="admin-empty">No refused pages. Refused pages are kept for 90 days.</p>
        ) : (
          <>
            <div className="scroll-x">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Boat</th>
                    <th>Board</th>
                    <th>Saved by</th>
                    <th>Check</th>
                    <th>Refused</th>
                    <th>Already there</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} data-testid={`page-refusal-${r.id}`}>
                      <td>
                        <Link
                          to={`/admin/neo/page-refusals/${r.id}`}
                          state={{ reason: openReason }}
                        >
                          {formatDate(r.createdAt)}
                        </Link>
                      </td>
                      <td>{r.boatName ?? '—'}</td>
                      <td>{r.boardSlug !== null ? <code>{r.boardSlug}</code> : 'Chat answer'}</td>
                      <td>{writerLabel(r.writer)}</td>
                      <td>
                        {checkLabel(r.check)}
                        {r.code === 'render_unavailable' ? (
                          <span className="admin-muted"> · {codeLabel(r.code)}</span>
                        ) : null}
                      </td>
                      <td>{r.refusals.length}</td>
                      <td>{r.kept.length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {nextBefore !== null ? (
              <p className="eval-pager">
                <button
                  type="button"
                  className="admin-link-button"
                  disabled={loadingMore}
                  onClick={() => void loadOlder()}
                >
                  {loadingMore ? 'Loading…' : 'Older →'}
                </button>
              </p>
            ) : null}
          </>
        )
      ) : null}
    </section>
  );
}
